/**
 * whack-a-mole-parts.js — greybox geometry for the Whack-a-Mole table.
 *
 * Pure builders, split out of `whack-a-mole.js` so that module stays gameplay
 * only. Each returns a fresh Object3D; the stall supplies positions and physics
 * descriptors, as it does for anything out of `assets3d/factory.js`.
 *
 * A mole exposes `userData.setType(type)` so the stall can recolour it in place
 * when a hole re-rolls, instead of rebuilding a body every pop.
 *
 * @module stalls/whack-a-mole-parts
 */

import * as THREE from 'three';
import { WHACK_A_MOLE, PALETTE } from '../constants.js';
import { mat } from '../assets3d/factory.js';

const CFG = WHACK_A_MOLE;

/** Body colour per mole type (GDD 6.6.2: bombs are red). */
const TYPE_COLOR = {
  standard: 0x8b6b47,
  golden: 0xffd700,
  bomb: 0xd0342c,
};

/**
 * The table the holes are cut into.
 * @returns {THREE.Mesh}
 */
export function createTable() {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(CFG.tableWidth, 0.09, CFG.tableDepth),
    mat(PALETTE.wood, { rough: 0.9 }),
  );
  m.name = 'mole-table';
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/**
 * The dark recess a mole sits in when it is down.
 * @returns {THREE.Mesh}
 */
export function createHole() {
  const m = new THREE.Mesh(
    new THREE.CylinderGeometry(CFG.holeRadius, CFG.holeRadius * 0.92, 0.24, 14, 1, true),
    new THREE.MeshStandardMaterial({
      color: 0x1a1410, roughness: 1, side: THREE.DoubleSide,
    }),
  );
  m.name = 'mole-hole';
  return m;
}

/**
 * A mole. Origin is its centre; the visible head sits above that.
 * @param {'standard'|'golden'|'bomb'} type
 * @returns {THREE.Group} with `userData.setType`.
 */
export function createMole(type = 'standard') {
  const g = new THREE.Group();
  g.name = 'mole';
  const r = CFG.moleRadius;

  const body = new THREE.Mesh(
    new THREE.CapsuleGeometry(r, CFG.moleHeight * 0.45, 4, 10),
    mat(TYPE_COLOR[type] || TYPE_COLOR.standard, { rough: 0.85 }),
  );
  body.castShadow = true;
  body.name = 'mole-body';
  g.add(body);

  const snout = new THREE.Mesh(
    new THREE.SphereGeometry(r * 0.34, 8, 6),
    mat(0xe8a0a0, { rough: 0.7 }),
  );
  snout.position.set(0, r * 0.1, -r * 0.85);
  g.add(snout);

  for (const s of [-1, 1]) {
    const eye = new THREE.Mesh(
      new THREE.SphereGeometry(r * 0.15, 8, 6),
      mat(0x120d0a, { rough: 0.3 }),
    );
    eye.position.set(s * r * 0.34, r * 0.5, -r * 0.7);
    g.add(eye);
  }

  // A fuse marks a bomb at a glance, so the red is not the only cue.
  const fuse = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 0.06, r * 0.06, r * 0.5, 6),
    mat(0x2b2b2b, { rough: 0.9 }),
  );
  fuse.position.y = CFG.moleHeight * 0.45 + r * 0.4;
  fuse.visible = type === 'bomb';
  g.add(fuse);

  g.userData.setType = (next) => {
    body.material = mat(TYPE_COLOR[next] || TYPE_COLOR.standard, { rough: 0.85 });
    fuse.visible = next === 'bomb';
  };
  g.userData.parts = { body, fuse };
  return g;
}

/**
 * The booth floor slab, so a dropped mallet-head lands on something.
 * @returns {THREE.Mesh}
 */
export function createFloorSlab() {
  const m = new THREE.Mesh(
    new THREE.BoxGeometry(3.4, 0.1, 3.4),
    mat(0x6a5a44, { rough: 1 }),
  );
  m.name = 'mole-floor';
  m.receiveShadow = true;
  return m;
}

/**
 * The 3×3 hole grid in stall-local space, front row first.
 * Row/column order is what gives a mole its stable index, so tests can address
 * `mole-4` and know they mean the centre hole.
 * @returns {Array<{x: number, y: number, z: number}>}
 */
export function holeGrid() {
  const out = [];
  const half = (CFG.gridSize - 1) / 2;
  for (let row = 0; row < CFG.gridSize; row++) {
    for (let col = 0; col < CFG.gridSize; col++) {
      out.push({
        x: (col - half) * CFG.holeSpacing,
        y: CFG.tableY,
        z: CFG.tableZ + (row - half) * CFG.holeSpacing,
      });
    }
  }
  return out;
}
